// Staff ringtone, shared by the dashboard (which arms it) and the incoming-call
// screen (which rings it).
//
// Three problems this solves, all found during the operator's field test of the
// installable window (roadmap #100):
//
// 1. PATH. The audio file used to be requested from the site root ('/ringtone.mp3'),
//    which resolves only where the app is served from the root: production serves it
//    from a subpath, where that URL is a 404 and the device could never ring at all.
//    The URL rides import.meta.env.BASE_URL, like the messaging service worker and the
//    identity redirects (roadmap #101 was the same class of defect).
//
// 2. AUTOPLAY. A browser refuses to START audio in a page with no user activation, and
//    the incoming-call screen opens by itself from a push while the window sits
//    minimised - exactly when the staff member most needs to hear it. Calling play() at
//    that moment is refused, silently. So the audio engine is ARMED instead: the
//    dashboard resumes an AudioContext on the staff member's first interaction and
//    decodes the tone into memory. A running context can start a sound at any later
//    moment, unattended, with no further activation - which is the whole point.
//
// 3. DIAGNOSABILITY. Every failure here used to be swallowed by an empty catch, so a
//    silent incoming call left no trace anywhere. Failures now log a one-line reason.
//
// The <audio> element remains as a fallback for anything that cannot run Web Audio, and
// still benefits from arming (the element is primed by the same gesture).
const RINGTONE_URL = `${import.meta.env.BASE_URL}ringtone.mp3`
const ELEMENT_ID = 'ringtone'

type Ctor = typeof AudioContext
const getAudioContextCtor = (): Ctor | null => {
  const w = window as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor }
  return w.AudioContext || w.webkitAudioContext || null
}

let context: AudioContext | null = null
let buffer: AudioBuffer | null = null
let source: AudioBufferSourceNode | null = null
let element: HTMLAudioElement | null = null

const getElement = (): HTMLAudioElement => {
  if (element) return element
  const existing = document.getElementById(ELEMENT_ID) as HTMLAudioElement | null
  const audio = existing ?? new Audio(RINGTONE_URL)
  audio.id = ELEMENT_ID
  audio.loop = true
  audio.preload = 'auto'
  ;(audio as unknown as { playsInline?: boolean }).playsInline = true
  element = audio
  return audio
}

/**
 * Arms the ringtone. Call from a user gesture (see StaffDashboard): resumes the audio
 * context and decodes the tone, so a later ring needs no user activation. Safe to call
 * repeatedly and safe to fail - ringing then falls back to the element, and the push
 * notification remains the alert either way.
 */
export const primeRingtone = async (): Promise<void> => {
  // Prime the element too: a muted play/pause marks it user-activated.
  const audio = getElement()
  audio.muted = true
  audio
    .play()
    .then(() => {
      audio.pause()
      audio.currentTime = 0
      audio.muted = false
    })
    .catch(() => {
      audio.muted = false
    })

  const Ctor = getAudioContextCtor()
  if (!Ctor) return
  try {
    if (!context) context = new Ctor()
    if (context.state === 'suspended') await context.resume()
    if (!buffer) {
      const response = await fetch(RINGTONE_URL)
      buffer = await context.decodeAudioData(await response.arrayBuffer())
    }
  } catch (err) {
    console.log('Ringtone could not be armed:', err)
  }
}

export const startRingtone = (): void => {
  // Preferred path: a running audio context can start a sound unattended.
  if (context && buffer && context.state === 'running') {
    try {
      stopRingtone()
      const node = context.createBufferSource()
      node.buffer = buffer
      node.loop = true
      node.connect(context.destination)
      node.start(0)
      source = node
      return
    } catch (err) {
      console.log('Ringtone could not start on the audio context:', err)
    }
  }

  const audio = getElement()
  audio.currentTime = 0
  audio.muted = false
  audio.play().catch((err) => {
    // Refused because the page never received a user gesture: the push notification
    // stays as the alert. Logged so a silent incoming call is diagnosable.
    console.log('Ringtone playback was refused:', err?.name || err)
  })
}

export const stopRingtone = (): void => {
  if (source) {
    try {
      source.stop()
    } catch {
      /* already stopped */
    }
    source.disconnect()
    source = null
  }
  const audio = element ?? (document.getElementById(ELEMENT_ID) as HTMLAudioElement | null)
  if (!audio) return
  audio.pause()
  audio.currentTime = 0
}
